/**
 * @desc 打卡相关接口
 * @author wansongtao
 */
// eslint-disable-next-line import/no-cycle
import request from '@/plugins/axios';
// eslint-disable-next-line import/no-cycle
import { wxAppId as appId } from '@/config/wxConfig';

/**
 * @description 上传微信录音到服务器
 * @param {string} media_id 微信录音ID
 * @returns {Promise}
 */
export const uploadWxRecord = mediaId => request({
  method: 'post',
  url: '/api/upload/upload_wx_voice',
  data: {
    media_id: mediaId,
    app_id: appId
  }
});

/**
 * @description 上传微信图片到服务器
 * @param {string} media_id 微信图片ID
 * @returns {Promise}
 */
export const uploadWxImg = mediaId => request({
  method: 'post',
  url: '/api/upload/upload_wx_image',
  data: {
    media_id: mediaId,
    app_id: appId
  }
});

/**
 * @description 打卡
 * @param {object} data 格式如下:
 * @param {number} data.visible_range 0/1  可见范围：0：所有人可见  1：仅管理员可见
 * @param {number} data.detail_id 所属单个训练营打卡小节ID
 * @param {string} data.content 打卡发表的文本内容
 * @param {string} [data.video_url] 视频链接地址，可选
 * @param {object[]} [data.multi_voices] 语音链接地址数组，可选
 * @param {object[]} [data.multi_images] 图片链接地址数组，可选
 * @returns {promise}
 */
export const clockIn = data => request({
  method: 'post',
  url: '/api/lesson/camp_punch/do_punch',
  data
});

/**
 * @description 对打卡进行点赞
 * @param {string} id 打卡ID
 * @returns {promise}
 */
export const like = id => request({
  method: 'post',
  url: '/api/lesson/camp_punch/do_like',
  data: {
    user_punch_id: id,
    is_like: 1
  }
});

/**
 * @description 对打卡取消点赞
 * @param {string} id 打卡ID
 * @returns {promise}
 */
export const dislike = id => request({
  method: 'post',
  url: '/api/lesson/camp_punch/do_like',
  data: {
    user_punch_id: id,
    is_like: 0
  }
});

/**
 * @description 打卡主题数据接口
 * @param {object} data
 * @param {number} data.detail_id 学习小节的id
 * @param {number} [data.class_id] 班级ID
 * @returns {Promise}
 */
export const clockInThemeApi = data => request({
  method: 'get',
  url: '/api/lesson/camp_punch/subject_detail',
  params: data
});

/**
 * @description 打卡日历数据
 * @param {number} id 系列课程ID
 * @returns {Promise}
 */
export const calendarApi = id => request({
  method: 'get',
  url: '/api/lesson/camp_punch/plan_calendar',
  params: {
    series_id: id
  }
});

/**
 * @description 用户的当天打卡数据
 * @param {number} id 打卡内容小节ID
 * @param {number} [pageSize = 20] 初始评论数量，默认20
 * @returns {Promise}
 */
export const myClockInData = (id, pageSize = 5) => request({
  method: 'get',
  url: '/api/lesson/camp_punch/my_punch_data',
  params: {
    detail_id: id,
    comment_limit: pageSize
  }
});

/**
 * @description 发表评论
 * @param {object} data 格式如下：
 * @param {string} data.content 评论内容
 * @param {string} data.user_punch_id 打卡ID
 * @param {string} [data.reply_id] 回复的评论ID
 * @returns {Promise}
 */
export const setComment = data => request({
  method: 'post',
  url: '/api/lesson/camp_punch/submit_comment',
  data
});

/**
 * @description 加载更多评论
 * @param {object} data 格式如下：
 * @param {string} data.up_id 打卡ID
 * @param {number} [data.limit = 20] 每页评论条数
 * @param {string} [data.last_id] 最后一条评论ID
 * @returns {Promise}
 */
export const getMoreComments = data => request({
  method: 'get',
  url: '/api/lesson/camp_punch/comment_query',
  params: data
});

/**
 *@description 用户打卡列表数据
 * @param {object} data 数据格式如下：
 * @param {number} data.detail_id 打卡内容小节ID
 * @param {number} [data.limit = 10] 每页条数，默认十条
 * @param {number} data.offset 从第几条开始
 * @param {number} [data.comment_limit = 20] 初始评论数量，默认二十
 * @param {number} [data.is_assistant_reply] 0 未点评， 1 已点评
 * @param {number} [data.need_mine] 0 不要自己的打卡数据，1 需要自己的打卡数据
 * @param {number} [data.group_id] 分组ID，不传返回所有分组
 * @returns {Promise}
 */
export const getOtherClockIn = data => request({
  method: 'get',
  url: '/api/lesson/camp_punch/member_punch_query',
  params: data
});

/**
 * @description 我的打卡个人信息
 * @param {number} id 课程ID
 * @returns {Promise}
 */
export const getMyClockInInfo = id => request({
  method: 'get',
  url: '/api/lesson/camp_punch/my_summary',
  params: {
    series_id: id
  }
});

/**
 * @description 我的打卡个人打卡列表
 * @returns {Promise}
 */
export const getMyClockInList = params => request({
  method: 'get',
  url: '/api/lesson/camp_punch/my_punch_query',
  params
});

/**
 * @description 获取打卡详情数据
 * @param {object} params
 * @param {string} params.up_id 打卡ID
 * @param {number} [params.comment_limit = 20] 初始评论数量
 * @returns
 */
export const getMyClockInDetails = params => request({
  method: 'get',
  url: '/api/lesson/camp_punch/one_punch_data',
  params
});

/**
 * @description 获取文件已上传进度
 * @param {string} hash 文件的唯一标识
 * @returns {promise}
 */
export const getAgoUploadLen = hash => request({
  method: 'get',
  url: '/api/upload/chunk_length',
  params: {
    hash
  }
});

/**
 * @description 上传文件分片
 * @param {object} params 请求头参数
 * @param {string} params.hash 文件的唯一标识
 * @param {number} params.offset 已上传文件长度
 * @param {number} params.length 本次上传文件长度
 * @param {object} data 上传的文件内容切片
 * @returns
 */
export const uploadSlice = (params, data) => request({
  method: 'post',
  url: '/api/upload/upload_chunk',
  params,
  data
});

/**
 * @description
 * @param {string} hash 文件的唯一标识
 * @param {string} type 文件类型，默认mp4
 * @returns {promise}
 */
export const submitCDN = (hash, type = 'mp4') => request({
  method: 'post',
  url: '/api/upload/finish_chunk_upload',
  data: {
    hash,
    file_type: type
  }
});

/**
 * @description 获取班级列表
 * @param {object} data 格式如下：
 * @param {number} [data.process = -1] 状态，-1全部、0未开营、1进行中、2已结营。默认-1
 * @param {number} [data.limit = 20] 每页条数，默认20条
 * @param {number} [data.offset = 0] 偏移量，默认0
 * @returns {promise}
 */
export const getClassList = (data = {}) => {
  const { process = -1, limit = 20, offset = 0 } = data;

  return new Promise((resolve, reject) => {
    request({
      method: 'get',
      url: '/api/lesson/camp_series/assistant_class',
      params: {
        process,
        limit,
        offset
      }
    }).then((res) => {
      if (res.success) {
        resolve(res.data);
      }

      reject();
    }).catch(() => reject());
  });
};

/**
 * @description 班级详情接口
 * @param {number} classId 班级ID
 * @returns {promise}
 */
export const getClassDetails = classId => new Promise((resolve, reject) => {
  request({
    method: 'get',
    url: '/api/lesson/camp_series/assistant_class_detail',
    params: {
      class_id: classId
    }
  }).then((res) => {
    if (res.success) {
      resolve(res.data);
    }

    reject();
  }).catch(() => reject());
});

/**
 * @description 获取班级学员列表
 * @param {object} data 格式如下：
 * @param {number} data.classId
 * @param {number} [data.limit = 10]
 * @param {number} [data.offset = 0]
 * @returns {promise}
 */
export const getStudents = (data = {}) => {
  const { limit = 10, offset = 0, classId } = data;

  return new Promise((resolve, reject) => {
    request({
      method: 'get',
      url: '/api/lesson/camp_series/class_student',
      params: {
        limit,
        offset,
        class_id: classId
      }
    }).then((res) => {
      if (res.success) {
        resolve(res.data);
      }

      reject();
    }).catch(() => reject());
  });
};

/**
 * @description 获取班级打卡列表
 * @param {number} classId 班级ID
 * @returns {promise}
 */
export const getClassClocks = classId => new Promise((resolve, reject) => {
  request({
    method: 'get',
    url: '/api/lesson/camp_series/class_punch_list',
    params: {
      class_id: classId
    }
  }).then((res) => {
    if (res.success) {
      resolve(res.data);
    }

    reject();
  }).catch(() => reject());
});

/**
 * @description 置顶/取消置顶
 * @param {string} cardId 打卡ID
 * @returns {promise}
 */
export const setCardTop = cardId => new Promise((resolve, reject) => {
  request({
    method: 'post',
    url: '/api/lesson/camp_punch/top_punch',
    data: {
      user_punch_id: cardId
    }
  }).then((res) => {
    if (res.success) {
      resolve();
    }

    reject();
  }).catch(() => reject());
});

/**
 * @description 获取打卡日签
 * @param {number} id 打卡小节ID
 * @returns {promise}
 */
export const getClockInPoster = id => new Promise((resolve, reject) => {
  request({
    method: 'get',
    url: '/api/lesson/camp_punch/daily_card',
    params: {
      detail_id: id
    }
  }).then((res) => {
    if (res.success) {
      resolve(res.data);
    }

    reject();
  }).catch(() => reject());
});

/**
 * @description 删除打卡
 * @param {string} id 打卡ID
 * @returns {promise}
 */
export const setDeleteClockIn = id => request({
  method: 'post',
  url: '/api/lesson/camp_punch/delete_punch',
  data: {
    user_punch_id: id
  }
});

/**
 * @description 获取班委管理的各个状态班级数量
 * @returns {promise} 成功resolve(data)，失败reject()
 * @returns {number} data.all_count 全部
 * @returns {number} data.not_start_count 未开营数量
 * @returns {number} data.start_count 进行中数量
 * @returns {number} data.finish_count 已结营数量
 */
export const getClassCount = () => new Promise((resolve, reject) => {
  request({
    method: 'get',
    url: '/api/lesson/camp_series/assistant_class_count'
  }).then((res) => {
    if (res.success) {
      resolve(res.data);
    }

    reject();
  }).catch(() => reject());
});

/**
 * @description 获取班级分组
 * @param {object} data
 * @param {number} data.detailId 打卡小节ID
 * @param {number} [data.classId] 班级ID
 * @returns {promise}
 */
export const getClassGroup = data => new Promise((resolve, reject) => {
  request({
    method: 'get',
    url: '/api/lesson/camp_punch/get_group_list',
    params: {
      ...data
    }
  }).then((res) => {
    if (res.success) {
      resolve(res.data);
    }

    reject();
  }).catch(() => reject());
});

/**
 * @description 加入班级分组
 * @param {object} data
 * @param {number} [data.class_id] 班级ID
 * @param {number} data.detail_id 打卡小结ID
 * @param {number} data.group_id 分组ID
 * @returns
 */
export const setClassGroup = data => request({
  method: 'post',
  url: '/api/lesson/camp_punch/join_group',
  data
});

/**
 * @description 获取用户信息
 * @returns
 */
export const getUserInfo = () => request({
  method: 'get',
  url: '/api/content/personal/center'
});
